/**
 * Spectator Store - Manages spectator room connection and state
 */

import { create } from 'zustand';
import { Client, Room } from 'colyseus.js';

interface SpectatorPlayer {
  wallet: string;
  username: string;
  score: number;
  hearts: number;
  jokerIds: string[];
}

interface SpectatorTurnResult {
  wallet: string;
  handType: string;
  score: number;
  turn: number;
}

interface SpectatorState {
  matchId: string;
  phase: string;
  currentTurn: number;
  maxTurns: number;
  wagerAmount: number;
  currency: string;
  players: Map<string, SpectatorPlayer>;
  turnResults: SpectatorTurnResult[];
}

interface SpectatorStore {
  // Connection state
  room: Room | null;
  connected: boolean;
  isWatching: boolean;
  watchingMatchId: string | null;

  // Match state
  matchState: SpectatorState | null;

  // Actions
  connect: (serverUrl: string) => Promise<void>;
  disconnect: () => void;
  watchMatch: (matchId: string) => void;
  stopWatching: () => void;
}

export const useSpectatorStore = create<SpectatorStore>((set, get) => ({
  room: null,
  connected: false,
  isWatching: false,
  watchingMatchId: null,
  matchState: null,

  connect: async (serverUrl: string) => {
    const client = new Client(serverUrl);
    try {
      const room = await client.joinOrCreate('spectator', {});

      // Listen for state updates
      room.onMessage('state_update', (state: SpectatorState) => {
        const players = new Map<string, SpectatorPlayer>();
        if (state.players && Array.isArray(state.players)) {
          state.players.forEach((p: SpectatorPlayer) => {
            players.set(p.wallet, p);
          });
        }
        set({
          matchState: {
            ...state,
            players,
          },
        });
      });

      room.onMessage('watching', (data: { matchId: string }) => {
        set({ isWatching: true, watchingMatchId: data.matchId });
      });

      room.onLeave(() => {
        set({
          room: null,
          connected: false,
          isWatching: false,
          watchingMatchId: null,
          matchState: null,
        });
      });

      set({ room, connected: true });
    } catch (error) {
      console.error('[SpectatorStore] Failed to connect:', error);
    }
  },

  disconnect: () => {
    const { room } = get();
    if (room) {
      room.leave();
    }
    set({
      room: null,
      connected: false,
      isWatching: false,
      watchingMatchId: null,
      matchState: null,
    });
  },

  watchMatch: (matchId: string) => {
    const { room } = get();
    if (room) {
      room.send('watch_match', { matchId });
    }
  },

  stopWatching: () => {
    set({ isWatching: false, watchingMatchId: null, matchState: null });
  },
}));
